import { useRef, useEffect } from 'react';

import type { DocNode } from '@atlaskit/adf-schema';
import type { ADFEntity } from '@atlaskit/adf-utils/types';

export function extractLinksFromADF(adf: DocNode): string[] {
	const linksSet = new Set<string>();

	function traverse(content: (ADFEntity | undefined)[] | undefined) {
		if (Array.isArray(content)) {
			for (const item of content) {
				if (!item) {
					continue;
				}
				switch (item.type) {
					case 'link':
						if (item.attrs?.href) {
							linksSet.add(item.attrs.href);
						}
						break;
					case 'blockCard':
					case 'inlineCard':
					case 'embedCard':
						if (item.attrs?.url) {
							linksSet.add(item.attrs.url);
						}
						break;
					default:
						// If the item has a 'content' property, recursively traverse it
						if (item.content) {
							traverse(item.content);
						}

						// If the item has a 'marks' property, recursively traverse it
						if (item.marks) {
							traverse(item.marks);
						}
				}
			}
		}
	}

	if (adf.content) {
		traverse(adf.content);
	}

	return Array.from(linksSet);
}

/**
 * Whenver the content argument changes, this hook returns a function reference which can be called on demand to extract the links
 * This way, the calculation doesn't necessarily run during render, and instead can be delayed to when it's actually needed
 * For example, with issue-reformatter (the only usecase as of now), we only do this calculation when the users click the prompt option
 */
export function useExtractedLinksRef(
	content?: string | DocNode,
): React.MutableRefObject<() => string[]> {
	const ref = useRef<() => string[]>(() => []);

	useEffect(() => {
		if (typeof content === 'object') {
			ref.current = () => extractLinksFromADF(content);
		}
	}, [content]);

	return ref;
}
